import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../../../App";
import { Card, CardContent } from "../../../components/ui/card";
import { Package, MapPin, BookOpen, ChefHat, AlertTriangle } from "lucide-react";

const fmt = (n) => `$${Number(n || 0).toLocaleString("es-MX", { maximumFractionDigits: 2 })}`;

export default function ResumenTab({ unidadSlug, color, colorSoft }) {
  const [data, setData] = useState({ items: 0, ubicaciones: 0, recetas: 0, menus: 0, valor_stock: 0, bajo_stock: 0, low_items: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const q = `?unidad_negocio=${unidadSlug}`;
        const [items, ubi, rec, menus, resumen] = await Promise.all([
          axios.get(`${API}/inventario/items${q}`).catch(() => ({ data: [] })),
          axios.get(`${API}/ubicaciones${q}`).catch(() => ({ data: [] })),
          axios.get(`${API}/recetas${q}`).catch(() => ({ data: [] })),
          axios.get(`${API}/menus${q}`).catch(() => ({ data: [] })),
          axios.get(`${API}/inventario/resumen${q}`).catch(() => ({ data: null })),
        ]);
        const lows = (items.data || []).filter((i) => (i.stock_actual || 0) <= (i.stock_minimo || 0)).slice(0, 5);
        setData({
          items: items.data?.length || 0,
          ubicaciones: ubi.data?.length || 0,
          recetas: rec.data?.length || 0,
          menus: (menus.data || []).filter((m) => m.nombre?.trim()).length,
          valor_stock: resumen.data?.valor_total || 0,
          bajo_stock: resumen.data?.bajo_stock ?? lows.length,
          low_items: lows,
        });
      } finally { setLoading(false); }
    };
    load();
  }, [unidadSlug]);

  if (loading) return <div className="text-stone-500 text-sm">Cargando...</div>;

  const kpis = [
    { icon: Package,  label: "Items en inventario", value: data.items,       color: "text-stone-900" },
    { icon: MapPin,   label: "Ubicaciones",         value: data.ubicaciones, color: "text-stone-900" },
    { icon: BookOpen, label: "Recetas",             value: data.recetas,     color: "text-stone-900" },
    { icon: ChefHat,  label: "Menús",               value: data.menus,       color: "text-stone-900" },
  ];

  return (
    <div className="space-y-4" data-testid={`resumen-${unidadSlug}`}>
      <Card className="border-stone-200" style={{ borderTop: `4px solid ${color}` }}>
        <CardContent className="p-5">
          <p className="text-xs text-stone-500 uppercase tracking-wide mb-2">Estado operativo</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {kpis.map((k) => {
              const KIcon = k.icon;
              return (
                <div key={k.label} className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: colorSoft }}>
                    <KIcon className="w-5 h-5" style={{ color }} />
                  </div>
                  <div>
                    <p className="text-[10px] text-stone-500 uppercase">{k.label}</p>
                    <p className={`font-display text-2xl ${k.color}`}>{k.value}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="border-emerald-200">
          <CardContent className="p-5">
            <p className="text-[10px] text-stone-500 uppercase tracking-wide mb-2">Valor de stock</p>
            <p className="font-display text-3xl text-emerald-800">{fmt(data.valor_stock)}</p>
            <p className="text-xs text-stone-500 mt-1">Costo promedio × stock actual</p>
          </CardContent>
        </Card>
        <Card className={data.bajo_stock > 0 ? "border-amber-200 bg-amber-50/40" : "border-stone-200"}>
          <CardContent className="p-5">
            <div className="flex items-center gap-2">
              {data.bajo_stock > 0 && <AlertTriangle className="w-5 h-5 text-amber-600" />}
              <p className="text-[10px] text-stone-500 uppercase tracking-wide">Alertas de stock</p>
            </div>
            <p className={`font-display text-3xl mt-2 ${data.bajo_stock > 0 ? "text-amber-700" : "text-stone-900"}`}>{data.bajo_stock}</p>
            {data.low_items.length > 0 && (
              <ul className="mt-2 space-y-1 text-xs text-stone-600">
                {data.low_items.map((i) => (
                  <li key={i.item_id} className="flex justify-between">
                    <span className="truncate">{i.nombre}</span>
                    <span className="text-amber-700 font-medium">{i.stock_actual} / min {i.stock_minimo}</span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="text-xs text-stone-500 p-3 rounded-lg" style={{ background: colorSoft }}>
        <p className="font-medium mb-1" style={{ color }}>Cómo operar esta unidad</p>
        <ol className="list-decimal ml-4 space-y-0.5">
          <li>Crea <strong>Ubicaciones</strong> (bodegas, cámaras, salones).</li>
          <li>Carga <strong>Items de inventario</strong> con stock actual y mínimo.</li>
          <li>Define <strong>Recetas</strong> con ingredientes vinculados al inventario (con sub-recetas y merma).</li>
          <li>Arma <strong>Menús</strong> combinando recetas. Calcula faltantes y genera órdenes de compra desde ahí.</li>
        </ol>
      </div>
    </div>
  );
}
